import type { ReactNode } from 'react'
import { useWindowDimensions, View } from 'react-native'
import { Avatar, Card, Icon, type MD3Theme, Text, useTheme } from 'react-native-paper'
import { formatRole, getRoleIcon } from '@/hooks/utils'
import { BREAKPOINTS } from '@/lib/config'
import { PB_SERVER_ADDR } from '@/lib/pocketbase'
import type { IconType } from '@/lib/types'

export interface UserCardUser {
    id: string
    name?: string
    email: string
    avatar?: string
    role?: string
    color?: string | null
    inactive?: boolean
}

export interface UserCardMeta {
    joinedDate?: string
    lastActive?: string
}

interface UserCardProps {
    user: UserCardUser
    meta?: UserCardMeta
    description?: ReactNode
    action?: ReactNode
    wrapper?: (children: ReactNode) => ReactNode
}

export function UserCard({ user, meta, description, action, wrapper }: UserCardProps) {
    const theme = useTheme()
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg
    const RoleIcon = user.role ? getRoleIcon(user.role) : null

    const content = (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, flex: 1 }}>
            <UserAvatar user={user} />
            <View style={{ flex: 1, gap: 4 }}>
                <UserInfo user={user} RoleIcon={RoleIcon} theme={theme} />
                {description}
            </View>
            {isDesktop && meta && <UserMeta meta={meta} theme={theme} />}
            {action}
        </View>
    )

    return (
        <Card style={{ padding: 16, opacity: user.inactive ? 0.6 : 1 }}>
            {wrapper ? wrapper(content) : content}
        </Card>
    )
}

function getInitials(name: string | undefined): string {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase()
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

function UserAvatar({ user }: { user: UserCardUser }) {
    if (user.avatar) {
        const avatarUrl = `${PB_SERVER_ADDR}/api/files/users/${user.id}/${user.avatar}`
        return <Avatar.Image source={{ uri: avatarUrl }} size={48} />
    }
    const colorStyle = user.color ? { backgroundColor: user.color } : undefined
    return <Avatar.Text label={getInitials(user.name)} size={48} style={colorStyle} />
}

function UserInfo({
    user,
    RoleIcon,
    theme,
}: {
    user: UserCardUser
    RoleIcon: IconType | null
    theme: MD3Theme
}) {
    return (
        <View style={{ gap: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 20, fontWeight: '600', color: theme.colors.onSurface }}>
                    {user.name || 'Unnamed User'}
                </Text>
                {user.inactive && (
                    <Text
                        style={{
                            fontSize: 11,
                            fontWeight: '600',
                            color: theme.colors.onErrorContainer,
                            backgroundColor: theme.colors.errorContainer,
                            paddingHorizontal: 6,
                            paddingVertical: 2,
                            borderRadius: 4,
                            overflow: 'hidden',
                        }}
                    >
                        Disabled
                    </Text>
                )}
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                {user.role && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        {RoleIcon && (
                            <Icon
                                source={RoleIcon}
                                size={14}
                                color={theme.colors.onSurfaceVariant}
                            />
                        )}
                        <Text
                            style={{
                                fontSize: 14,
                                fontWeight: '500',
                                color: theme.colors.onSurface,
                            }}
                        >
                            {formatRole(user.role)}
                        </Text>
                    </View>
                )}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Icon source="email" size={14} color={theme.colors.onSurfaceVariant} />
                    <Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
                        {user.email}
                    </Text>
                </View>
            </View>
        </View>
    )
}

function UserMeta({ meta, theme }: { meta: UserCardMeta; theme: MD3Theme }) {
    return (
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
            {meta.joinedDate && (
                <Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
                    Joined {meta.joinedDate}
                </Text>
            )}
            {meta.lastActive && (
                <Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
                    Last Active {meta.lastActive}
                </Text>
            )}
        </View>
    )
}
